import { DatabricksDashboard } from '@databricks/aibi-client';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// Components
import Text, { TextVariant } from 'src/components/atoms/Text';
import { ShowState } from 'src/components/molecules/ShowState';

// API
import { defaultInstance } from 'src/api/instance';
import { configVars } from 'src/helpers';
import { useUserFeature } from 'src/hooks';

// Styles
import { Container, DashboardContainer, ErrorContainer, LoadingContainer } from './styles';

interface DatabricksToken {
  access_token: string;
  expires_in: number;
  token_type: string;
}

const NinjaReportingPage: React.FC = () => {
  const hasNinjaReportingAccess = useUserFeature('ninja_reporting');
  const dashboardClientRef = useRef<DatabricksDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchToken = useCallback(async (targetDashboardId: string) => {
    try {
      // Get Databricks token from backend
      const tokenResponse: DatabricksToken = await defaultInstance.get(
        `${configVars.server_url}/v2/databricks/token?dashboard_id=${targetDashboardId}`
      );

      if (!tokenResponse.access_token) {
        throw new Error('Failed to obtain Databricks token');
      }

      return tokenResponse.access_token;
    } catch (err) {
      console.error('Failed to fetch Databricks token:', err);
      throw err;
    }
  }, []);

  const initializeDashboard = useCallback(async () => {
    if (dashboardClientRef.current) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get dashboard ID and workspace ID from config
      const targetDashboardId =
        configVars.databricks.embedded_dashboards.ninja_reporting_dashboard_id;
      const { base_url: baseUrl, workspace_id: workspaceId } = configVars.databricks;

      if (!baseUrl || !targetDashboardId || !workspaceId) {
        throw new Error('Databricks configuration is missing');
      }

      const accessToken = await fetchToken(targetDashboardId);

      // Initialize the DatabricksDashboard
      if (!containerRef.current) {
        throw new Error('Container element not found');
      }

      const client = new DatabricksDashboard({
        workspaceId,
        instanceUrl: baseUrl,
        container: containerRef.current,
        dashboardId: targetDashboardId,
        token: accessToken,
        colorScheme: 'light',
        getNewToken: () => fetchToken(targetDashboardId)
      });

      dashboardClientRef.current = client;

      // Initialize the dashboard
      client.initialize();
    } catch (err) {
      console.error('Failed to initialize Databricks dashboard:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      dashboardClientRef.current = null;
    } finally {
      setIsLoading(false);
    }
  }, [fetchToken]);

  useEffect(() => {
    if (!hasNinjaReportingAccess) {
      return undefined;
    }

    initializeDashboard();

    return () => {
      if (dashboardClientRef.current) {
        dashboardClientRef.current.destroy();
        dashboardClientRef.current = null;
      }
    };
  }, [hasNinjaReportingAccess, initializeDashboard]);

  if (!hasNinjaReportingAccess) {
    return (
      <Container>
        <ShowState
          type="information"
          variant="error"
          message="You do not have permission to access Ninja Reporting."
        />
      </Container>
    );
  }

  return (
    <Container>
      {/* Loading State */}
      {isLoading && (
        <LoadingContainer>
          <Text variant={TextVariant.normal}>Loading Ninja Reporting...</Text>
        </LoadingContainer>
      )}

      {/* Error State */}
      {error && (
        <ErrorContainer>
          <Text variant={TextVariant.normal} style={{ color: 'red' }}>
            Error loading Ninja Reporting: {error}
          </Text>
        </ErrorContainer>
      )}

      {/* Dashboard Container */}
      <DashboardContainer ref={containerRef} isVisible={!isLoading && !error} />
    </Container>
  );
};

export default NinjaReportingPage;
